import { createSelector } from '@reduxjs/toolkit';
import { functionsNotDeleted } from 'src/store/selectors/responsibilities/functions-not-deleted';
import { employeesNotDeleted } from 'src/store/selectors/employees/employees-not-deleted';
import { employeesDeleted } from 'src/store/selectors/employees/employees-deleted';
import { contactsNotDeleted } from 'src/store/selectors/partners/contacts-not-deleted';
import { contactsDeleted } from 'src/store/selectors/partners/contacts-deleted';
import { functionsDeleted } from 'src/store/selectors/responsibilities/functions-deleted';
import type { Assignee, PronounForm } from 'src/library/fields/assignee';
import { employeesShortNames } from 'src/store/selectors/employees/employees-short-names';
import { partnersNotDeleted } from 'src/store/selectors/partners/partners-not-deleted';
import { partnersDeleted } from 'src/store/selectors/partners/partners-deleted';
import { sortBy } from 'lodash';
import { currentUserUuid } from 'src/store/selectors/user/currentUserUuid';
import { joinWithAnd } from 'src/utilities/text';

export const peopleAndFunctions = createSelector(
  functionsNotDeleted,
  functionsDeleted,
  employeesNotDeleted,
  employeesDeleted,
  employeesShortNames,
  contactsNotDeleted,
  contactsDeleted,
  partnersNotDeleted,
  partnersDeleted,
  currentUserUuid,
  function (
    functionsNotDeleted,
    functionsDeleted,
    employeesNotDeleted,
    employeesDeleted,
    employeesShortNames,
    contactsNotDeleted,
    contactsDeleted,
    partnersNotDeleted,
    partnersDeleted,
    currentUserUuid,
  ): Assignee[] {
    const employees: Assignee[] = employeesNotDeleted.concat(employeesDeleted).map((item) => {
      let shortName = item.name;
      const shortNameObject = employeesShortNames.find((e) => e.uuid === item.uuid);
      if (shortNameObject) {
        shortName = shortNameObject.name;
      }
      return {
        type: 'employee',
        uuid: item.uuid,
        fullName: item.name,
        shortName,
        isCurrentUser: item.uuid === currentUserUuid,
      };
    });
    const functions: Assignee[] = functionsNotDeleted.concat(functionsDeleted).map((item) => {
      let functionAssignees: Assignee[] = [];
      if (item.employees) {
        functionAssignees = employees.filter((e) => {
          return item.employees.includes(e.uuid);
        });
      }
      return {
        type: 'function',
        uuid: item.uuid,
        fullName: item.name ?? '',
        shortName: item.name ?? '',
        functionAssignees,
      };
    });
    const contacts: Assignee[] = contactsNotDeleted.concat(contactsDeleted).map((item) => {
      let name = item.firstName;
      if (name) {
        if (item.lastName) {
          name += ' ' + item.lastName;
        }
      } else {
        name = item.lastName;
      }
      const partner = partnersNotDeleted.concat(partnersDeleted).find((p) => {
        return p.uuid === item.partnerUuid;
      });
      if (partner) {
        name += ' (' + partner.name + ')';
      }
      return {
        type: 'contact',
        uuid: item.uuid,
        fullName: name,
        shortName: name,
      };
    });
    return sortBy(functions, [(item) => item.fullName.toLowerCase()]).concat(
      sortBy(employees, [(item) => item.fullName.toLowerCase()]),
      sortBy(contacts, [(item) => item.fullName.toLowerCase()]),
    );
  },
);

function assigneeNames(assignees: Assignee[], fullNames: boolean, pronoun: PronounForm): string {
  if (pronoun) {
    assignees = sortBy(assignees, [(a) => (a.isCurrentUser ? 1 : 0)]);
  }
  return joinWithAnd(
    assignees.map((item) => {
      if (pronoun && item.isCurrentUser) {
        if (pronoun === 'subject') {
          return 'jeg';
        }
        if (pronoun === 'object') {
          return 'meg';
        }
      }
      let name = item.shortName;
      if (fullNames) {
        name = item.fullName;
      }
      if (item.functionAssignees) {
        return name + ' (' + assigneeNames(item.functionAssignees, fullNames, pronoun) + ')';
      }
      return name;
    }),
  );
}

export function getAssigneesAsText(
  peopleAndFunctions: Assignee[],
  assignees: string[],
  fullNames: boolean,
  pronoun: PronounForm = undefined,
): string {
  if (peopleAndFunctions.length) {
    let list: Assignee[] = [...peopleAndFunctions];
    if (pronoun) {
      list = sortBy(list, [(p) => (p.isCurrentUser ? 1 : 0), (p) => p.shortName]);
    }
    return assigneeNames(
      list.filter((item) => {
        return assignees.includes(item.uuid);
      }),
      fullNames,
      pronoun,
    );
  }
  return '';
}
